import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
  DrawerTrigger,
  Icon,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Switch,
  Text,
  toastManager,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type Color,
  type IconName,
  toColor,
} from "@salestrak/ui";

import { useIsPhoneClass } from "#app/lib/hooks/use-phone-class";
import { useOverlayHandoff } from "#app/lib/hooks/use-overlay-handoff";
import { useAutomations } from "#app/hooks/automations/use-automations";
import { useAutomationMutations } from "#app/hooks/automations/use-automation-mutations";
import { useAutomationCatalog } from "#app/hooks/automations/use-automation-catalog";
import { useAutomationPermissions } from "#app/hooks/automations/use-automation-permissions";
import { triggerEventLabel } from "#app/components/automations/trigger-sentence";
import { stepDisplayLabel } from "#app/components/automations/canvas/automation-step-labels";
import { automationToggleToast } from "#app/components/automations/automation-status";
import { useAutomationsDialogActions } from "#app/lib/automations-dialog-context";
import {
  useInboundSourceResolver,
  useAutomationConnectionDisabled,
} from "#app/hooks/automations/use-automation-inbound-sources";
import { useOutboundTargetResolver } from "#app/hooks/automations/use-automation-outbound-targets";
import {
  inboundTriggerPhrase,
  outboundStepPhrase,
} from "#app/hooks/automations/integration-node-phrases";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import { EntityTag } from "#app/components/entity";

type AutomationItem = ReturnType<typeof useAutomations>["automations"][number];
type StepTypeDef = ReturnType<typeof useAutomationCatalog>["stepTypes"][number];

// How many automation cards show before the grid caps; the "+N more" ghost
// card and the manage bar are the paths to the rest. With the create card, 8
// fills three 3-across rows exactly (over the cap, one card cedes its cell to
// the ghost so the grid still closes on full rows).
const CARD_CAP = 8;

// Every card in the grid is this tall — uniform cells whether a name wraps to
// two lines or the create card carries its caption. ~148×82 per card at the
// popover's 488px width.
const CARD_HEIGHT = 82;

// The phone sheet's resting height.
const SHEET_SNAP_POINTS = [0.6];

// A compressed "what it does" line: friendly trigger event → the first real
// action, labeled exactly as the builder's node selects ("Stage changed →
// Update assignees"). Surfaces as the card's hover tooltip. No live trigger
// entity here, so the action subject defaults to "Project" (same as Manage).
function automationSentence(
  item: AutomationItem,
  stepTypes: StepTypeDef[],
  // Names a connection-bound trigger/step from the integration pickers, so a
  // card reads "Connection triggered from AccuLynx SW Contracts → Log
  // connection action with AccuLynx SW Contracts" instead of repeating the
  // generic catalog labels on every integration automation.
  resolveInbound: (entityType: string, id: string) => string | null,
  resolveOutbound: (entityType: string, id: string) => string | null,
): string {
  const triggerNode = item.triggers?.[0] ?? null;
  const trigger = triggerNode
    ? inboundTriggerPhrase(
        triggerNode,
        resolveInbound,
        triggerEventLabel(triggerNode.sourceKey),
      )
    : "No trigger yet";
  const steps = [...(item.steps ?? [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
  );
  const defFor = (type: string) => stepTypes.find((d) => d.key === type);
  // Prefer the first non-control step — "If / else" as the action label says
  // nothing about what the automation does.
  const action = steps.find((s) => defFor(s.type)?.category !== "control") ?? steps[0];
  if (!action) return trigger;
  const label = outboundStepPhrase(
    action,
    resolveOutbound,
    stepDisplayLabel(action.type, defFor(action.type)?.label ?? action.type, "Project"),
    "Project",
  );
  return `${trigger} → ${label}`;
}

// One automation as a grid card: icon tile top-left, switch top-right, name
// (two lines, then truncated) at the bottom. Live cards wear the soft green
// wash + tinted border — the feature's Live vocabulary; off cards sit flat and
// muted. The card body is pressable (opens the builder) with the what-it-does
// sentence as its hover tooltip; the Switch is OUTSIDE the pressable region
// (absolutely positioned over the card) so toggling never also opens.
function AutomationCard({
  item,
  sentence,
  onOpen,
  onToggle,
  canEdit,
  canToggle,
  connectionDisabled,
}: {
  item: AutomationItem;
  sentence: string;
  onOpen: (id: string) => void;
  onToggle: (id: string, enabled: boolean) => void;
  // Capability gates (use-automation-permissions): without `canToggle` the
  // switch disappears (the green wash + border still say Live); without
  // `canEdit` the card press routes to the read-only list, not the builder.
  canEdit: boolean;
  canToggle: boolean;
  // Its source integration connection is disabled — resolved at grid level (one
  // query watcher, not one per card). Such an automation cannot run, so its
  // switch is inert and says why.
  connectionDisabled: boolean;
}) {
  const live = item.enabled;
  const lastRan =
    live && item.lastRunAt ? formatRelativeDate(item.lastRunAt, "compact") : null;
  const tooltip = connectionDisabled
    ? `${sentence} — can't run: the integration connection it's triggered by is disabled.`
    : lastRan && lastRan !== "now"
      ? `${sentence} · ran ${lastRan}`
      : sentence;
  return (
    <Box
      position="relative"
      height={CARD_HEIGHT}
      rounded="xl"
      border
      // DESIGN-TUNABLE: there is no `success-subtle` token, so the live
      // card's wash + hairline are authored from the `success` token.
      className={live ? "border-success/32 bg-success/10" : undefined}
      borderColor={live ? undefined : "border"}
      // Off cards sit FLAT on the popover (no fill — the border alone
      // delineates). Any mismatched fill reads badly in dark mode: the
      // `background` token (18%) is a black well against the 27% popover,
      // and even `accent` (30%) reads as a murky patch. Only live cards
      // carry a wash — green = the Live vocabulary.
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <Box<"button">
              align="stretch"
              aria-label={
                canEdit
                  ? `Edit automation ${item.name}`
                  : `View automation ${item.name}`
              }
              as="button"
              cursor="pointer"
              fill="width"
              // Floor between the icon/toggle row and the name — with
              // space-between, gap acts as the minimum when a 2-line name
              // would otherwise close the distance.
              gap="sm"
              hoverBackground
              justify="between"
              onClick={() => onOpen(item.id)}
              padding="sm"
              rounded="xl"
            />
          }
        >
            {/* The automation's own (non-theme) glyph on a raised neutral
                tile — border + sharp shadow break it off the card wash, à la
                the ClickUp reference. State lives in the card, not the icon. */}
            <Box
              shadow="sm" width={24}
              height={24}
              rounded="md"
              align="center"
              justify="center"
              // `card` matches the popover surface, so the tile reads as a
              // neutral raised chip on the card wash in dark mode instead of
              // a dark hole (`background` is 9 points darker there).
              background="muted"
              border
              borderColor="border"
            >
              <Icon
                color={toColor(item.icon?.color) ?? "warning"}
                name={(item.icon?.icon as IconName) ?? "zap"}
                size="xs"
              />
            </Box>
            {/* Full foreground on off cards too — off is a state the switch
                and flat card already carry, not a reason to fade the name. */}
            <Text size="xs" truncate={2} weight="medium">
              {item.name}
            </Text>
        </TooltipTrigger>
        <TooltipPopup side="top">
          <Text size="xs">{tooltip}</Text>
        </TooltipPopup>
      </Tooltip>
      <Box
        position="absolute"
        top={8}
        right={8}
        direction="row"
        align="center"
        gap="xs"
      >
        {canToggle ? (
          <Switch
            aria-label={
              connectionDisabled
                ? "Cannot run — the integration connection this automation is triggered by is disabled"
                : live
                  ? "Turn off this Automation"
                  : "Turn on this Automation"
            }
            checked={live}
            disabled={connectionDisabled}
            onCheckedChange={(v) => onToggle(item.id, v)}
          />
        ) : null}
      </Box>
    </Box>
  );
}

// The grid's leading cell, same fixed size as the automation cards. The ONLY
// theme-colored icon in the grid: a solid brand-blue tile with an on-color
// plus (à la ClickUp's create tile) so the create action pops against the
// non-theme automation icons. Brand, not primary — primary is near-black/white
// and would read as a void here.
function CreateCard({ onClick }: { onClick: () => void }) {
  return (
    <Box<"button">
      align="stretch"
      aria-label="Add Automation"
      as="button"
      border
      borderColor="border"
      cursor="pointer"
      gap="sm"
      height={CARD_HEIGHT}
      hoverBackground
      justify="between"
      onClick={onClick}
      // Flat on the popover like the off cards (see AutomationCard) — the
      // brand plus-tile inside is what makes this cell pop.
      padding="sm"
      rounded="xl"
    >
      <Box
        width={24}
        height={24}
        rounded="md"
        align="center"
        justify="center"
        // Brand is outside Box's semantic background set (it is not a surface
        // token), so the solid brand fill is an authored class.
        className="bg-accent"
      >
        {/* Bumped size + stroke: the default 1.5-stroke 12px glyph reads as a
            faint hairline on the saturated brand fill. */}
        {/* On-brand foreground: Icon paints `currentColor`. */}
        <Icon
          className="text-accent-foreground"
          name="plus"
          size="sm"
          strokeWidth={2.5}
        />
      </Box>
      <Text size="xs" weight="medium">
        Add Automation
      </Text>
    </Box>
  );
}

// Overflow cell when the workflow has more automations than the grid caps at:
// a quiet dashed ghost card that hands off to the Manage tab. Takes the last
// cell so the grid always closes on full rows.
function MoreCard({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <Box<"button">
      borderStyle="dashed" align="center"
      aria-label={`View all automations (${count} more)`}
      as="button"
      border
      borderColor="border"
      cursor="pointer"
      gap="2xs"
      height={CARD_HEIGHT}
      hoverBackground
      justify="center"
      onClick={onClick}
      rounded="xl"
    >
      <Text size="xs" variant="muted" weight="medium">
        +{count} more
      </Text>
      <Text size="2xs" variant="muted">
        View all in Manage
      </Text>
    </Box>
  );
}

// The workflow header's Automations action. Renders the zap trigger (amber +
// count when this workflow has active automations) and a ClickUp-style card
// popover: title + workflow location, a 3-across card grid (2-across in the
// mobile sheet) led by a create card, and a full-width "Manage automations ·
// N total active" bar. Create/manage/edit deep-link into the Automations
// dialog.
export function WorkflowAutomationsPopover({
  workflowId,
}: {
  workflowId: string;
}) {
  const [open, setOpen] = useState(false);
  const { handOff, finalFocus, onOpenChange: clearHandoff } = useOverlayHandoff();
  const handleOpenChange = useCallback(
    (next: boolean) => {
      clearHandoff(next);
      setOpen(next);
    },
    [clearHandoff],
  );
  // Building an automation needs the desktop canvas, so the sheet variant
  // hides its create affordances — they'd dead-end on the read-only list.
  // Phone-class (rotation-stable), not width-bucket: a landscape phone must
  // not regrow the create card.
  const isMobile = useIsPhoneClass();
  const automationsWhere = useMemo(() => ({ workflowId }), [workflowId]);
  const { automations } = useAutomations(automationsWhere);
  const { stepTypes } = useAutomationCatalog();
  // Name connection-bound triggers/steps in each card's sentence.
  const resolveInbound = useInboundSourceResolver();
  // One watcher for the whole grid — never a hook inside AutomationCard.
  const resolveConnectionDisabled = useAutomationConnectionDisabled();
  const resolveOutbound = useOutboundTargetResolver();
  const { canCreate, canEdit, canToggle } = useAutomationPermissions();
  const { updateAutomation, goLiveVersion } = useAutomationMutations();
  const { openDialog: openAutomations } = useAutomationsDialogActions();
  const { workflowMap } = useWorkspaceData();
  const workflow = workflowMap.get(workflowId);
  const workflowName = workflow?.name ?? "this workflow";

  // Active first, then alphabetical, so the most relevant cards sit on top.
  // Off automations with no live-ready version don't earn a card at all: the
  // popover is the quick on/off surface, and a card whose switch can only
  // refuse is noise. Unfinished automations live in Manage, where the
  // needs-attention alert explains what's missing.
  const scoped = useMemo(
    () =>
      automations
        .filter((a) => a.enabled || (a.versions ?? []).some((v) => v.isLiveReady))
        .sort((a, b) => {
          if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
          return a.name.localeCompare(b.name);
        }),
    [automations],
  );
  const activeCount = useMemo(
    () => scoped.filter((a) => a.enabled).length,
    [scoped],
  );
  // Over the cap, the last cell becomes the "+N more" ghost card — so one
  // automation card yields its spot and the grid still closes on full rows.
  const capped = scoped.length > CARD_CAP;
  const visible = capped ? scoped.slice(0, CARD_CAP - 1) : scoped;
  const overflow = scoped.length - visible.length;

  // All three actions close this overlay and open the automations dialog in its
  // place, so the trigger must not pull focus back out of the dialog — see
  // useOverlayHandoff.
  // Close first, then hand off: the dialog must not mark the background
  // aria-hidden while this overlay still holds focus. Closing inside the
  // hand-off instead would put both in one tick, and whether the popover has
  // unmounted by then is down to commit ordering — it holds on a first open and
  // not on a second.
  const handleCreate = useCallback(() => {
    setOpen(false);
    handOff(() => openAutomations({ type: "create-for-workflow", workflowId }));
  }, [handOff, openAutomations, workflowId]);

  const handleManage = useCallback(() => {
    setOpen(false);
    handOff(() => openAutomations({ type: "manage-for-workflow", workflowId }));
  }, [handOff, openAutomations, workflowId]);

  const handleEdit = useCallback(
    (id: string) => {
      setOpen(false);
      handOff(() => openAutomations({ type: "edit-automation", automationId: id }));
    },
    [handOff, openAutomations],
  );

  // Same toggle semantics as the Manage list (automations-list.tsx):
  //  - Turn OFF: immediate, answered by the standard toggle toast.
  //  - Turn ON, one version: go live with the live (else sole) version.
  //  - Turn ON, 2+ versions: which version goes live is a real choice — hand
  //    off to Manage, whose row owns the "Turn on — which version?" picker
  //    (one picker implementation, not a popover fork).
  const handleToggle = useCallback(
    async (id: string, enabled: boolean) => {
      const item = automations.find((a) => a.id === id);
      if (!item) return;
      if (enabled) {
        if ((item.versions?.length ?? 0) > 1 && !item.liveVersionId) {
          handleManage();
          return;
        }
        const versionId = item.liveVersionId ?? item.versions?.[0]?.id;
        if (!versionId) return;
        const result = await goLiveVersion(id, versionId);
        if (!result.ok) {
          toastManager.add({ title: result.error, type: "error" });
          return;
        }
        const { title, description } = automationToggleToast(true, {
          liveSince: result.version.publishedAt,
          runCount7d: item.runCount7d,
        });
        toastManager.add({ title, description, type: "success" });
        return;
      }
      const updated = await updateAutomation(id, { enabled });
      if (updated) {
        const { title, description } = automationToggleToast(false, {
          liveSince: item.liveVersion?.publishedAt,
          runCount7d: item.runCount7d,
        });
        toastManager.add({ title, description, type: "success" });
      }
    },
    [automations, goLiveVersion, updateAutomation, handleManage],
  );

  const sentenceFor = useCallback(
    (item: AutomationItem) =>
      automationSentence(item, stepTypes, resolveInbound, resolveOutbound),
    [stepTypes, resolveInbound, resolveOutbound],
  );

  // An explicit `useIsPhoneClass()` Drawer/Popover branch over ONE shared body.
  // The title is authored TWICE on purpose: a decorative heading in the
  // popover, the accessible name of a modal surface in the drawer.
  const trigger = (
    <Button
      aria-label="Automations"
      // The icon size variant collapses the padding when there is no count.
      size={activeCount === 0 ? "icon-sm" : "sm"}
      variant="ghost"
    >
      {/* lucide ships a single outline set, so this glyph never reads as
        * filled (DESIGN-TUNABLE). */}
      <Icon
        color={activeCount > 0 ? "warning" : "muted"}
        name="zap"
        size="sm"
      />
      {activeCount > 0 ? (
        <Text size="xs" weight="medium">
          {activeCount}
        </Text>
      ) : null}
    </Button>
  );

  const body = (
    <>
        {scoped.length === 0 ? (
          // Empty state: a quiet pitch with one clear way forward.
          <Box
            align="center"
            paddingTop="lg"
            paddingBottom="lg"
            paddingX="md"
            gap="xs"
          >
            <Box
              marginBottom="sm" width={40}
              height={40}
              rounded="lg"
              align="center"
              justify="center"
              background="muted"
            >
              <Icon color="warning" name="zap" size="md" />
            </Box>
            <Text size="sm" truncate={1} weight="semibold">
              Automate {workflowName}
            </Text>
            <Text align="center" size="xs" variant="muted">
              Run actions automatically as projects move through this
              workflow.
            </Text>
            {isMobile || !canCreate ? (
              <Box paddingTop="xs">
                <Text align="center" size="xs" variant="muted">
                  {isMobile
                    ? "Create automations for this workflow on desktop."
                    : "A member with the manage permission can set these up."}
                </Text>
              </Box>
            ) : (
              <Box paddingTop="md">
                <Button onClick={handleCreate} size="sm" variant="outline">
                  <Text size="sm" weight="medium">
                    Add Automation
                  </Text>
                </Button>
              </Box>
            )}
          </Box>
        ) : (
          <Box padding="md" gap="sm">
            {/* Title + location. The workflow chip truncates so long names
                never push past the popover edge. */}
            {/* leading="none" on every text piece pins each line box to its
                font size, so the mixed 14/12px parts center on one optical
                line instead of jittering on differing default leadings. */}
            {/* paddingBottom tops up the container's sm gap to 16px so the
                space under the title matches the md padding above it. */}
            <Box
              direction="row"
              align="center"
              gap="xs"
              minWidth={0}
              paddingBottom="sm"
            >
              <Text size="sm" weight="semibold" leading="none">
                Automations
              </Text>
              <Text leading="none" size="xs" variant="muted">
                for projects in
              </Text>
              <Box fill="flex" minWidth={0} direction="row" justify="start">
                {/* Styled to read as part of the sentence: 12px muted regular
                    label, bare 12px glyph (no padded tile). */}
                <EntityTag
                  entity={{
                    kind: "workflow",
                    id: workflowId,
                    workflow: workflow ?? { name: "Workflow" },
                  }}
                  size="xs"
                  weight="regular"
                  mutedLabel
                  padded={false}
                  interactive={false}
                />
              </Box>
            </Box>

            <Box display="grid" columns={2} gap="sm" className="sm:grid-cols-3">
              {!isMobile && canCreate && <CreateCard onClick={handleCreate} />}
              {visible.map((item, index) => (
                <AutomationCard
                  key={`${item.id}-${index}`}
                  item={item}
                  sentence={sentenceFor(item)}
                  // Read-only viewers still see the cards (which automations
                  // exist + which are live); pressing one hands off to the
                  // read-only Manage list instead of the builder.
                  onOpen={canEdit ? handleEdit : () => handleManage()}
                  onToggle={(id, enabled) => {
                    void handleToggle(id, enabled);
                  }}
                  canEdit={canEdit}
                  canToggle={canToggle}
                  connectionDisabled={resolveConnectionDisabled(item.triggers)}
                />
              ))}
              {overflow > 0 ? (
                <MoreCard count={overflow} onClick={handleManage} />
              ) : null}
            </Box>

            {/* marginTop tops up the container's sm gap to 16px — same
                breathing room as above/below the title, so the grid sits
                evenly framed between header and manage bar. */}
            <Box<"button">
              marginTop="sm" align="center"
              aria-label="Manage automations"
              as="button"
              border
              borderColor="border"
              cursor="pointer"
              direction="row"
              gap="xs"
              hoverBackground
              justify="center"
              onClick={handleManage}
              paddingX="md"
              paddingY="sm"
              rounded="lg"
            >
              <Icon color="muted" name="settings" size="xs" />
              <Text size="xs" weight="medium">
                Manage automations
              </Text>
              <Text size="xs" variant="muted">
                ·
              </Text>
              <Text
                size="xs"
                variant={activeCount > 0 ? "success" : "muted"}
                weight="medium"
              >
                {activeCount} total active
              </Text>
            </Box>
          </Box>
        )}
    </>
  );

  if (isMobile) {
    return (
      <>
        <Drawer
          onOpenChange={handleOpenChange}
          open={open}
          snapPoints={SHEET_SNAP_POINTS}
        >
          <DrawerTrigger render={trigger} />
          <DrawerPopup finalFocus={finalFocus} showBar>
            <DrawerHeader>
              <DrawerTitle>Automations</DrawerTitle>
            </DrawerHeader>
            <DrawerPanel>{body}</DrawerPanel>
          </DrawerPopup>
        </Drawer>
      </>
    );
  }

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <Tooltip>
        <TooltipTrigger render={<PopoverTrigger render={trigger} />} />
        <TooltipPopup side="top">
          <Text size="xs">Automations</Text>
        </TooltipPopup>
      </Tooltip>
      {/* The stock viewport insets its content; these bodies pad themselves. */}
      <PopoverPopup
        align="end"
        padding="none"
        className={`${scoped.length === 0 ? "w-90" : "w-122"}`}
        finalFocus={finalFocus}
        side="bottom"
      >
        {body}
      </PopoverPopup>
    </Popover>
  );
}
